import Drawer from '@mui/material/Drawer'
import { deleteNote, getNoteById } from 'api/notes'
import { setStatusSharedItem } from 'api/teams'
import { setStatus } from 'api/timelines'
import { defaultDrawerSX } from 'const'
import { UIPencilImg } from 'features/UI'
import React from 'react'
import { useMutation, useQuery } from 'react-query'
import { useActions, useAppDispatch } from 'store/hooks'
import { getSharedTimelineById } from 'store/slice/sharedTimeline.slice'
import { ETimelinePostStatus, ETimelinePostType, Note } from 'types'
import { useTeams } from '../../../features/Timeline/hooks/useTeams'
import { useAuthContext } from '../../context/AuthProvider'
import NotificationSys from '../../NotificationSystem'
import DrawerContainer from '../common/DrawerContainer'
import DrawerHeader from '../common/DrawerHeader'
import DrawerNoteItem from './DrawerNoteItem'

interface Props {
  id: number | null
  timelineId: number | null
  isShared: boolean
  handleClose: () => void
}

interface ContentProps {
  data: Note
  showActions: boolean
  isShared: boolean
  onDelete: (id: number) => void
  onUpdateStatus: (status: ETimelinePostStatus) => void
  handleClose: () => void
}

const Content = (props: ContentProps) => {
  const { data, showActions, isShared, onDelete, handleClose, onUpdateStatus } = props
  const title = data.subject
  const author = data.authorName || 'Unknown Author'

  const date = data.createdAt
  const text = data.body
  const link = data.link

  const onDeleteWrapper = () => {
    onDelete(data.id)
  }
  const onReviewLaterWrapper = () => {
    onUpdateStatus(ETimelinePostStatus.review)
  }
  const onDoneWrapper = () => {
    onUpdateStatus(ETimelinePostStatus.done)
  }

  return (
    <>
      <DrawerHeader handleClose={handleClose} borderEnable={true}>
        <UIPencilImg />
        <span>Note</span>
      </DrawerHeader>

      <DrawerNoteItem
        id={data.id}
        title={title}
        author={author}
        date={date}
        text={text}
        link={link}
        showActions={showActions}
        isShared={isShared}
        onDelete={onDeleteWrapper}
        onDone={onDoneWrapper}
        onReviewLater={onReviewLaterWrapper}
      />
    </>
  )
}

const ViewNoteDrawer = (props: Props) => {
  const dispatch = useAppDispatch()
  const { id, handleClose, isShared } = props
  const { activeTeamId } = useTeams()

  const isOpen = id !== null

  const { user } = useAuthContext()
  const { getTimelineById } = useActions()
  const userId = user?.id ?? -1

  const query = useQuery(['getNoteById', { id }], () => getNoteById(id ?? -1), {
    enabled: isOpen,
    select: (req) => req.data,
    cacheTime: 0,
    staleTime: 0,
  })

  const removeMutation = useMutation(deleteNote, {
    onSuccess: () => {
      handleClose()
      getTimelineById({})
      NotificationSys.showSuccess(`Note successfully removed`)
    },
  })

  const setStatusMutation = useMutation(setStatus, {
    onSuccess: () => {
      handleClose()
      getTimelineById({})
      NotificationSys.showSuccess(`Note successfully updated`)
    },
  })

  const setSharedStatusMutation = useMutation(setStatusSharedItem, {
    onSuccess: () => {
      handleClose()
      if (activeTeamId === null || !userId) return
      dispatch(getSharedTimelineById({ userId, teamId: activeTeamId }))
      NotificationSys.showSuccess(`Note successfully updated`)
    },
  })

  const onUpdateStatus = (status: ETimelinePostStatus) => {
    if (isShared && id) {
      setSharedStatusMutation.mutate({
        entityId: id,
        status,
        type: ETimelinePostType.note,
      })

      return
    }
    setStatusMutation.mutate({
      id: id ?? -1,
      status,
      type: ETimelinePostType.note,
    })
  }

  const { data, isLoading } = query

  return (
    <Drawer anchor="right" open={isOpen} onClose={handleClose} sx={defaultDrawerSX}>
      <DrawerContainer
        isOpen={isOpen}
        hasData={!!data}
        loading={isLoading || removeMutation.isLoading}
        handleClose={handleClose}
      >
        {data && (
          <Content
            data={data}
            showActions={userId === data.authorId}
            isShared={isShared}
            onDelete={removeMutation.mutate}
            handleClose={handleClose}
            onUpdateStatus={onUpdateStatus}
          />
        )}
      </DrawerContainer>
    </Drawer>
  )
}

export default ViewNoteDrawer
